import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosClient from "../../axiosClient";
import WorkoutCard from "../../components/WorkoutCard/WorkoutCard";
import { SquaresPlusIcon } from "@heroicons/react/24/outline";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import auth from "../../fire";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import SupersetLogoHeader from "../../components/SupersetLogoHeader/SupersetLogoHeader";
import CreateWorkoutLogModal from "../../components/CreateWorkoutLogModal/CreateWorkoutLogModal";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";
import FullWidthButton from "../../components/FullWidthButton/FullWidthButton";
import MessageModal from "../../components/MessageModal/MessageModal";
import EditWorkoutDetailsModal from "../../components/EditWorkoutDetailsModal/EditWorkoutDetailsModal";
import AddWorkoutToProgramModal from "../../components/AddWorkoutToProgramModal/AddWorkoutToProgramModal";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState<string | null>(null);
  const [user, loading] = useAuthState(auth);
  const [isCreateWorkoutModalOpen, setIsCreateWorkoutModalOpen] =
    useState(false);
  const [isEditWorkoutModalOpen, setIsEditWorkoutModalOpen] = useState(false);
  const [workoutToEdit, setWorkoutToEdit] = useState<Workout | null>(null);
  const [isAddToProgramModalOpen, setIsAddToProgramModalOpen] = useState(false);
  const [workoutToAddToProgram, setWorkoutToAddToProgram] =
    useState<Workout | null>(null);
  const [highlightedWorkout, setHighlightedWorkout] = useState<{
    id: string;
    type: "indigo" | "red";
  } | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });
  const { innerHeight: screenHeight } = window;

  const [previousWorkouts, setPreviousWorkouts] = useState<string[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);

  const getWorkouts = useCallback(async () => {
    try {
      const getWorkoutsResponse = await axiosClient.get(`/api/workouts/`);
      const workouts = getWorkoutsResponse.data;

      setWorkouts(workouts);
      setIsInitialLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Getting Workout Logs",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(false);
    }
  }, []);

  const getListOfUsersWorkouts = useCallback(async () => {
    try {
      const getListOfUserWorkoutsResponse = await axiosClient.post(
        `/api/workouts/all-user-workouts`,
      );
      const workouts = getListOfUserWorkoutsResponse.data;
      setPreviousWorkouts(workouts);
    } catch (e: any) {
      console.error(e);
    }
  }, []);

  const getPrograms = useCallback(async () => {
    try {
      const getProgramsResponse = await axiosClient.get(`/api/programs`, {
        params: { populateWorkoutTemplates: true },
      });
      const programs = getProgramsResponse.data;
      setPrograms(programs);
    } catch (e: any) {
      console.error(e);
    }
  }, []);

  const getCreateWorkoutModalData = useCallback(() => {
    getListOfUsersWorkouts();
    getPrograms();
  }, [getListOfUsersWorkouts, getPrograms]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    getWorkouts();
    getCreateWorkoutModalData();
  }, [user, loading, getWorkouts, navigate, getCreateWorkoutModalData]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const highlightWorkout = (
    workoutId: string,
    type: "indigo" | "red",
    onComplete?: () => void,
  ) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setHighlightedWorkout({ id: workoutId, type });
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedWorkout(null);
      highlightTimeoutRef.current = null;
      onComplete?.();
    }, 600);
  };

  const handleEditWorkout = (workoutId: string) => {
    const workoutToEdit = workouts.find((workout) => workout.id === workoutId);
    setWorkoutToEdit(workoutToEdit || null);
    setIsEditWorkoutModalOpen(true);
  };

  const handleAddToProgram = (workoutId: string) => {
    const workout = workouts.find((workout) => workout.id === workoutId);
    setWorkoutToAddToProgram(workout || null);
    setIsAddToProgramModalOpen(true);
  };

  const handleRepeatWorkout = async (workoutId: string) => {
    try {
      const duplicateResponse = await axiosClient.post(
        `/api/workouts/duplicate/${workoutId}`,
      );

      if (duplicateResponse.status === 200) {
        const newWorkout = duplicateResponse.data;
        setWorkouts((prevWorkouts) => [...prevWorkouts, newWorkout]);
        highlightWorkout(newWorkout.id, "indigo");
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Repeating Workout",
        message: `${errorMessage} Please try again or contact support.`,
      });
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setWorkoutToDelete(null);
  };

  const handleDeleteModalSubmit = async () => {
    const deletingWorkoutId = workoutToDelete;
    setIsDeleteModalOpen(false);
    setWorkoutToDelete(null);

    if (!deletingWorkoutId) return;

    highlightWorkout(deletingWorkoutId, "red", async () => {
      setWorkouts((prev) =>
        prev.filter((workout) => workout.id !== deletingWorkoutId),
      );

      try {
        const deleteWorkoutResponse = await axiosClient.delete(
          `/api/workouts/${deletingWorkoutId}`,
        );

        if (deleteWorkoutResponse.status !== 200) {
          throw new Error(deleteWorkoutResponse.data.message);
        }
      } catch (e: any) {
        console.error(e);
        const errorMessage =
          e?.response?.data?.error || "Something unexpected occured.";
        setModalState({
          isOpen: true,
          type: "error",
          title: "Error Deleting Workout",
          message: `${errorMessage} Please try again or contact support.`,
        });
        setIsInitialLoading(true);
        getWorkouts();
      }
    });
  };

  const handleInitiateDeleteWorkout = (workoutId: string) => {
    setWorkoutToDelete(workoutId);
    setIsDeleteModalOpen(true);
  };

  const handleEditModalClose = (updatedWorkoutId?: string) => {
    setIsEditWorkoutModalOpen(false);
    setWorkoutToEdit(null);

    if (updatedWorkoutId) {
      getWorkouts();
      highlightWorkout(updatedWorkoutId, "indigo");
    }
  };

  const LOGO_HEADER = 63;
  const WORKOUTS_TITLE = 44;
  const NEW_WORKOUT_BTN = 70;
  const MAIN_NAV = 63;
  const OTHER_CONTENT_HEIGHT =
    LOGO_HEADER + WORKOUTS_TITLE + NEW_WORKOUT_BTN + MAIN_NAV;

  const amountOfCardSkeletons = useMemo(() => {
    const listHeight = screenHeight - OTHER_CONTENT_HEIGHT;
    return Math.ceil(listHeight / 62);
  }, [OTHER_CONTENT_HEIGHT, screenHeight]);

  return (
    <div className="h-full relative">
      <SupersetLogoHeader />
      <h1 className="p-2 font-bold text-gray-900 text-xl tracking-tight">
        Workouts
      </h1>
      {isInitialLoading ? (
        <>
          <div
            className="px-2 overflow-hidden"
            style={{ height: `calc(100vh - ${OTHER_CONTENT_HEIGHT}px)` }}
          >
            {Array.from({ length: amountOfCardSkeletons }, (number, index) => (
              <TextSkeleton key={index} height="62px" className="w-full mb-1" />
            ))}
          </div>
        </>
      ) : (
        <div
          className="px-2 pr-0 overflow-y-scroll"
          style={{ height: `calc(100vh - ${OTHER_CONTENT_HEIGHT}px)` }}
        >
          {workouts.length === 0 && (
            <div className="flex items-center h-full">
              <div className="flex flex-col items-center mr-2 mt-[28px] mb-24 w-full">
                <SquaresPlusIcon
                  className="h-10 w-10 text-gray-400"
                  aria-hidden="true"
                />
                <h2 className="text-sm font-medium text-gray-900 mt-2 mb-1">
                  No workouts
                </h2>
                <p className="text-sm text-gray-500">
                  Start tracking by creating a new workout.
                </p>
              </div>
            </div>
          )}
          {workouts.length > 0 &&
            workouts
              .sort((a, b) => {
                return Date.parse(b.date) - Date.parse(a.date);
              })
              .map((workout) => {
                return (
                  <WorkoutCard
                    className={"mr-2"}
                    key={workout.id}
                    id={workout.id}
                    title={workout.title || "Untitled Workout"}
                    date={workout.date}
                    onEditWorkout={handleEditWorkout}
                    onRepeatWorkout={handleRepeatWorkout}
                    onAddToProgram={handleAddToProgram}
                    onDeleteWorkout={handleInitiateDeleteWorkout}
                    highlightType={
                      highlightedWorkout?.id === workout.id
                        ? highlightedWorkout.type
                        : null
                    }
                  />
                );
              })}
        </div>
      )}
      <div className="py-4 px-2 w-full bg-gray-50 absolute bottom-0">
        <FullWidthButton
          text="New Workout"
          icon="PlusIcon"
          onClick={() => {
            setIsCreateWorkoutModalOpen(true);
          }}
          isInitialLoading={isInitialLoading}
        />
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onModalClose={handleDeleteModalClose}
        onSubmit={handleDeleteModalSubmit}
        deleteTitle={"Delete workout"}
        deleteMessage={
          "Are you sure you want to delete? This workout and all its exercise data will be permanently removed. This action cannot be undone."
        }
      />
      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
      <CreateWorkoutLogModal
        isOpen={isCreateWorkoutModalOpen}
        onModalClose={() => {
          setIsCreateWorkoutModalOpen(false);
        }}
        previousWorkouts={previousWorkouts}
        programs={programs}
      />
      <EditWorkoutDetailsModal
        isOpen={isEditWorkoutModalOpen}
        onModalClose={handleEditModalClose}
        workoutToEdit={workoutToEdit}
        previousWorkouts={previousWorkouts}
      />
      <AddWorkoutToProgramModal
        isOpen={isAddToProgramModalOpen}
        onModalClose={() => {
          setIsAddToProgramModalOpen(false);
          setWorkoutToAddToProgram(null);
        }}
        workout={workoutToAddToProgram}
        programs={programs}
      />
    </div>
  );
};

export default Home;
